import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import Calendar from "../../components/ui/calendar1";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { RadioGroup, RadioGroupItem } from "../../components/ui/radio-group";
import {
  CalendarIcon,
  IndianRupee,
  CheckCircle2,
  Receipt,
  Info,
  AlertTriangle,
  ArrowRightLeft,
  Building2,
  Landmark,
  Plus,
  Hash,
  ChevronLeft,
  ChevronRight,
  Eye,
  EyeOff,
  User as UserIcon,
} from "lucide-react";
import { format } from "date-fns";
import { cn } from "../../lib/utils";
import { LenderBankAccount, ScheduleLine } from "../../types/schema";
import { Switch } from "../../components/ui/switch";
import { Separator } from "../../components/ui/separator";
import { Checkbox } from "../../components/ui/checkbox";
import {
  buildInterimScheduleLineFromPreview,
  previewInterimFromSchedule,
  resolveTdsRatePercent,
} from "../../lib/calculations";
import apiService from "../../services/apiService";
import { toast } from "react-toastify";
import LenderBankAccountsSheet from "../../screens/ManageLenders/LenderBankAccountsSheet";

export type CollectionRoute = "direct" | "pass_through";

export type NoAApproveConfirmPayload = {
  disbursementDate: string;
  disbursementAmount: number;
  updatedScheduleLines: ScheduleLine[];
  interimLine: ScheduleLine | null;
  collectionRoute: CollectionRoute;
  lenderBankAccountId: string;
  lenderBankAccount: LenderBankAccount;
};

interface NoAApproveModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  noaId: string;
  fundingCap: number;
  lender?: string;
  lenderId?: string | number;
  onConfirm: (payload: NoAApproveConfirmPayload) => Promise<void>;
  isSubmitting: boolean;
  scheduleLines: ScheduleLine[];
  /** When `assign_later`, interim was created at schedule approval — hide interim UI. */
  assignmentMode?: string | null;
}

const maskAccount = (n: string) => {
  if (!n) return "";
  if (n.length <= 4) return `••${n}`;
  return `••••${n.slice(-4)}`;
};

const normalizeBankAccount = (raw: Record<string, unknown>): LenderBankAccount => {
  const accountTypeRaw = String(raw.accountType ?? raw.account_type ?? "current").toLowerCase();
  return {
    id: String(raw.id ?? raw.bank_account_id ?? ""),
    lender: String(raw.lender ?? raw.lender_name ?? ""),
    customerId: raw.customerId ? String(raw.customerId) : raw.customer_id ? String(raw.customer_id) : undefined,
    accountType: accountTypeRaw === "virtual" ? "virtual" : "current",
    accountHolderName: String(raw.accountHolderName ?? raw.account_holder_name ?? ""),
    bankName: String(raw.bankName ?? raw.bank_name ?? ""),
    branchName: String(raw.branchName ?? raw.branch_name ?? ""),
    accountNumber: String(raw.accountNumber ?? raw.account_number ?? ""),
    ifscCode: String(raw.ifscCode ?? raw.ifsc_code ?? ""),
    createdAt: String(raw.createdAt ?? raw.created_at ?? new Date().toISOString()),
    createdBy: raw.createdBy ? String(raw.createdBy) : raw.created_by ? String(raw.created_by) : undefined,
  };
};

function BankAccountPickRow({
  account,
  selected,
}: {
  account: LenderBankAccount;
  selected: boolean;
}) {
  const [revealed, setRevealed] = useState(false);

  const toggleReveal = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setRevealed((v) => !v);
  };

  return (
    <label
      htmlFor={`noa-bank-${account.id}`}
      className={cn(
        "flex items-start gap-3 rounded-md border p-3 cursor-pointer transition-colors",
        selected ? "border-primary bg-primary/5" : "border-border hover:bg-muted/50"
      )}
    >
      <RadioGroupItem id={`noa-bank-${account.id}`} value={account.id} className="mt-1" />
      <div className="flex-1 min-w-0 space-y-1.5">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-medium truncate">{account.bankName}</span>
          <Badge variant="outline" className="text-[10px] capitalize shrink-0">
            {account.accountType === "current" ? "Current" : "Virtual"}
          </Badge>
        </div>
        <p className="text-xs text-muted-foreground truncate">{account.branchName}</p>
        <div className="flex items-center justify-between gap-2 text-xs">
          <div className="flex items-center gap-1.5 text-muted-foreground min-w-0">
            <UserIcon className="h-3 w-3 shrink-0" />
            <span className="truncate text-foreground">{account.accountHolderName || "—"}</span>
          </div>
        </div>
        <div className="flex items-center justify-between gap-2 text-xs">
          <div className="flex items-center gap-1.5 text-muted-foreground min-w-0">
            <Hash className="h-3 w-3 shrink-0" />
            <span className="shrink-0">A/C No.</span>
            <span className="font-mono text-foreground truncate">
              {revealed ? account.accountNumber : maskAccount(account.accountNumber)}
            </span>
          </div>
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className="h-6 w-6 shrink-0"
            onClick={toggleReveal}
            title={revealed ? "Hide account number" : "View account number"}
          >
            {revealed ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
          </Button>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Landmark className="h-3 w-3 shrink-0" />
          <span className="shrink-0">IFSC</span>
          <span className="font-mono text-foreground">{account.ifscCode}</span>
        </div>
      </div>
    </label>
  );
}

export default function NoAApproveModal({
  open,
  onOpenChange,
  noaId,
  fundingCap,
  lender,
  lenderId,
  onConfirm,
  isSubmitting,
  scheduleLines,
  assignmentMode,
}: NoAApproveModalProps) {
  const [step, setStep] = useState<1 | 2>(1);
  const [disbursementDate, setDisbursementDate] = useState<Date | undefined>(new Date());
  const [disbursementAmount, setDisbursementAmount] = useState<string>(fundingCap.toString());
  const [chargeInterim, setChargeInterim] = useState<boolean>(true);
  const [overrideInterimBase, setOverrideInterimBase] = useState(false);
  const [interimBaseOverrideAmount, setInterimBaseOverrideAmount] = useState("");
  const [collectionRoute, setCollectionRoute] = useState<CollectionRoute>("direct");
  const [lenderBankAccountId, setLenderBankAccountId] = useState("");
  const [availableBankAccounts, setAvailableBankAccounts] = useState<LenderBankAccount[]>([]);
  const [loadingBankAccounts, setLoadingBankAccounts] = useState(false);
  const [bankSheetOpen, setBankSheetOpen] = useState(false);

  const isAssignLater = String(assignmentMode ?? "").toLowerCase() === "assign_later";

  const regularScheduleLines = useMemo(
    () => scheduleLines.filter((l) => !l.isInterim),
    [scheduleLines]
  );

  const resolvedTdsRatePercent = useMemo(
    () => resolveTdsRatePercent(scheduleLines),
    [scheduleLines]
  );

  const interimPreview = useMemo(() => {
    if (isAssignLater || regularScheduleLines.length === 0 || !disbursementDate) {
      return null;
    }
    return previewInterimFromSchedule(
      regularScheduleLines,
      format(disbursementDate, "yyyy-MM-dd"),
      resolvedTdsRatePercent
    );
  }, [isAssignLater, regularScheduleLines, disbursementDate, resolvedTdsRatePercent]);

  const fetchBankAccounts = useCallback(async () => {
    if (!lenderId) {
      setAvailableBankAccounts([]);
      return;
    }
    setLoadingBankAccounts(true);
    try {
      const response = await apiService.get<{ data?: unknown } | unknown>(
        `/lender/get-lender-bank-accounts/${lenderId}`
      );
      const root = response as Record<string, unknown>;
      const raw = root?.data ?? response;
      const list: unknown[] = Array.isArray(raw)
        ? raw
        : Array.isArray((raw as { accounts?: unknown[] })?.accounts)
          ? (raw as { accounts: unknown[] }).accounts
          : [];
      const mapped = list.map((item) => normalizeBankAccount(item as Record<string, unknown>));
      setAvailableBankAccounts(mapped);
      setLenderBankAccountId((prev) => {
        if (prev && mapped.some((a) => a.id === prev)) return prev;
        return mapped[0]?.id ?? "";
      });
    } catch (error) {
      console.error("Failed to fetch lender bank accounts:", error);
      toast.error("Failed to load lender bank accounts");
      setAvailableBankAccounts([]);
      setLenderBankAccountId("");
    } finally {
      setLoadingBankAccounts(false);
    }
  }, [lenderId]);

  useEffect(() => {
    if (!open) {
      setStep(1);
      setDisbursementDate(new Date());
      setDisbursementAmount(fundingCap.toString());
      setChargeInterim(true);
      setOverrideInterimBase(false);
      setInterimBaseOverrideAmount("");
      setCollectionRoute("direct");
      setLenderBankAccountId("");
      setAvailableBankAccounts([]);
    }
  }, [open, fundingCap]);

  useEffect(() => {
    if (open && lenderId) {
      void fetchBankAccounts();
    }
  }, [open, lenderId, fetchBankAccounts]);

  useEffect(() => {
    setOverrideInterimBase(false);
    setInterimBaseOverrideAmount("");
  }, [disbursementDate]);

  useEffect(() => {
    if (interimPreview && !overrideInterimBase) {
      setInterimBaseOverrideAmount(String(interimPreview.baseRentRounded));
    }
  }, [interimPreview, overrideInterimBase]);

  const interimApplicable = !!interimPreview;
  const effectiveInterimBase = useMemo(() => {
    if (!interimPreview) return 0;
    return Math.ceil(parseFloat(interimBaseOverrideAmount) || interimPreview.baseRentRounded);
  }, [interimPreview, interimBaseOverrideAmount]);

  const isStep1Valid =
    Boolean(disbursementDate) &&
    Boolean(disbursementAmount) &&
    parseFloat(disbursementAmount) > 0;

  const isStep2Valid = Boolean(lenderBankAccountId);

  const selectedBankAccount = useMemo(
    () => availableBankAccounts.find((a) => a.id === lenderBankAccountId),
    [availableBankAccounts, lenderBankAccountId]
  );

  const handleConfirm = async () => {
    if (!disbursementDate || !disbursementAmount || isSubmitting || !selectedBankAccount) return;

    let updatedScheduleLines: ScheduleLine[] = [];
    let interimLine: ScheduleLine | null = null;

    if (!isAssignLater && interimApplicable && chargeInterim && interimPreview) {
      const baseOverride = parseFloat(interimBaseOverrideAmount) || interimPreview.baseRentRounded;
      interimLine = buildInterimScheduleLineFromPreview(
        interimPreview,
        baseOverride,
        resolvedTdsRatePercent
      );
      updatedScheduleLines.push(interimLine);
    }

    updatedScheduleLines = [...updatedScheduleLines, ...regularScheduleLines];

    try {
      await onConfirm({
        disbursementDate: format(disbursementDate, "yyyy-MM-dd"),
        disbursementAmount: parseFloat(disbursementAmount),
        updatedScheduleLines,
        interimLine,
        collectionRoute,
        lenderBankAccountId,
        lenderBankAccount: selectedBankAccount,
      });
      onOpenChange(false);
    } catch {
      // Parent shows error toast; keep modal open for retry
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
              Approve Notice of Assignment
            </DialogTitle>
            <DialogDescription>
              Confirm approval for <span className="font-medium">{noaId}</span>.
              {step === 1
                ? " Enter disbursement details below."
                : " Choose how collections are routed and the lender bank account."}
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-center gap-2 text-xs">
            <div
              className={cn(
                "flex items-center gap-1.5 rounded-full px-3 py-1 border",
                step === 1 ? "border-primary bg-primary/5 text-primary font-medium" : "border-border text-muted-foreground"
              )}
            >
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary/10 text-[10px] font-semibold">1</span>
              Disbursement
            </div>
            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <div
              className={cn(
                "flex items-center gap-1.5 rounded-full px-3 py-1 border",
                step === 2 ? "border-primary bg-primary/5 text-primary font-medium" : "border-border text-muted-foreground"
              )}
            >
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary/10 text-[10px] font-semibold">2</span>
              Collection
            </div>
          </div>

          {step === 1 && (
            <>
              <div className="space-y-4 py-2">
                <div className="space-y-2">
                  <Label htmlFor="disbursement-date">Disbursement Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        id="disbursement-date"
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !disbursementDate && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {disbursementDate ? format(disbursementDate, "PPP") : "Select date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar value={disbursementDate} onChange={setDisbursementDate} />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="disbursement-amount">Disbursement Amount</Label>
                  <div className="relative">
                    <IndianRupee className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="disbursement-amount"
                      type="number"
                      placeholder="0.00"
                      className="pl-10"
                      value={disbursementAmount}
                      onChange={(e) => setDisbursementAmount(e.target.value)}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Funding Cap: ₹{fundingCap.toLocaleString("en-IN")}
                  </p>
                </div>
              </div>

              {!isAssignLater && regularScheduleLines.length > 0 && (
                <div className="space-y-3 rounded-lg border bg-muted/30 p-4">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-medium flex items-center gap-2">
                      <Receipt className="h-4 w-4 text-primary" /> Interim Rental
                    </Label>
                    {interimApplicable && (
                      <Switch checked={chargeInterim} onCheckedChange={setChargeInterim} />
                    )}
                  </div>

                  {!interimApplicable && (
                    <div className="flex items-start gap-2 text-xs text-muted-foreground">
                      <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                      <span>
                        Disbursement date is on/after first due date — no interim rental required. Billing starts from the first regular due date.
                      </span>
                    </div>
                  )}

                  {interimApplicable && chargeInterim && interimPreview && (
                    <>
                      <div className="grid grid-cols-2 gap-3 text-xs">
                        <div className="space-y-0.5">
                          <div className="text-muted-foreground">Interim Period</div>
                          <div className="font-medium text-foreground">
                            {disbursementDate ? format(disbursementDate, "dd MMM yyyy") : "—"} →{" "}
                            {format(new Date(new Date(interimPreview.firstDueDate).getTime() - 86400000), "dd MMM yyyy")}
                          </div>
                        </div>
                        <div className="space-y-0.5">
                          <div className="text-muted-foreground">Interim Days</div>
                          <div className="font-medium text-foreground">{interimPreview.interimDays} days</div>
                        </div>
                        <div className="space-y-0.5">
                          <div className="text-muted-foreground">First Due Date</div>
                          <div className="font-medium text-foreground">
                            {format(new Date(interimPreview.firstDueDate), "dd MMM yyyy")}
                          </div>
                        </div>
                        <div className="space-y-0.5">
                          <div className="text-muted-foreground">Interim Base (auto)</div>
                          <div className="font-semibold text-primary">
                            ₹{interimPreview.baseRentRounded.toLocaleString("en-IN")}
                          </div>
                        </div>
                      </div>

                      <Separator />

                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="noa-override-interim-base"
                          checked={overrideInterimBase}
                          onCheckedChange={(checked) => {
                            const on = checked === true;
                            setOverrideInterimBase(on);
                            if (on) {
                              setInterimBaseOverrideAmount(String(interimPreview.baseRentRounded));
                            }
                          }}
                        />
                        <Label htmlFor="noa-override-interim-base" className="text-xs font-normal cursor-pointer">
                          Override base amount
                        </Label>
                      </div>

                      {overrideInterimBase && (
                        <div className="space-y-1">
                          <Label htmlFor="noa-interim-base" className="text-xs text-muted-foreground">
                            Interim base amount
                          </Label>
                          <div className="relative">
                            <IndianRupee className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <Input
                              id="noa-interim-base"
                              type="number"
                              min={0}
                              step="1"
                              className="pl-10 h-9"
                              value={interimBaseOverrideAmount}
                              onChange={(e) => setInterimBaseOverrideAmount(e.target.value)}
                              placeholder="Enter interim base amount"
                            />
                          </div>
                        </div>
                      )}

                      <div className="flex items-center justify-between text-xs pt-1 border-t">
                        <span className="text-muted-foreground">Final interim base</span>
                        <span className="font-bold">₹{effectiveInterimBase.toLocaleString("en-IN")}</span>
                      </div>
                    </>
                  )}

                  {interimApplicable && !chargeInterim && (
                    <div className="flex items-start gap-2 text-xs text-amber-700 dark:text-amber-300">
                      <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                      <span>
                        No interim will be charged. Billing starts from first due date — customer gets free usage for{" "}
                        {interimPreview?.interimDays} day(s).
                      </span>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {step === 2 && (
            <div className="space-y-4 py-2">
              <div className="space-y-3 rounded-lg border bg-muted/30 p-4">
                <div className="space-y-1">
                  <Label className="text-sm font-medium flex items-center gap-2">
                    <ArrowRightLeft className="h-4 w-4 text-primary" /> Collection Route
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Decides who the customer pays. Applies to every invoice generated under this NoA.
                  </p>
                </div>
                <RadioGroup
                  value={collectionRoute}
                  onValueChange={(v) => setCollectionRoute(v as CollectionRoute)}
                  className="grid grid-cols-1 gap-2"
                >
                  <label
                    htmlFor="route-direct"
                    className={cn(
                      "flex items-start gap-3 rounded-md border p-3 cursor-pointer transition-colors",
                      collectionRoute === "direct" ? "border-primary bg-primary/5" : "border-border hover:bg-muted/50"
                    )}
                  >
                    <RadioGroupItem id="route-direct" value="direct" className="mt-0.5" />
                    <div className="space-y-0.5">
                      <div className="text-sm font-medium flex items-center gap-2">
                        <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                        Direct to Lender <Badge variant="outline" className="text-[10px]">Default</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Customer pays lender (base) and Astrafin (GST − TDS) on their own bank lines.
                      </p>
                    </div>
                  </label>
                  <label
                    htmlFor="route-pass"
                    className={cn(
                      "flex items-start gap-3 rounded-md border p-3 cursor-pointer transition-colors",
                      collectionRoute === "pass_through" ? "border-primary bg-primary/5" : "border-border hover:bg-muted/50"
                    )}
                  >
                    <RadioGroupItem id="route-pass" value="pass_through" className="mt-0.5" />
                    <div className="space-y-0.5">
                      <div className="text-sm font-medium flex items-center gap-2">
                        <ArrowRightLeft className="h-3.5 w-3.5 text-muted-foreground" />
                        Pass-through via Astrafin
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Customer pays Astrafin in full. Astrafin remits the lender share onward — tracked under Payments → Lender Remittances.
                      </p>
                    </div>
                  </label>
                </RadioGroup>
              </div>

              <div className="space-y-3 rounded-lg border bg-muted/30 p-4">
                <div className="flex items-start justify-between gap-2">
                  <Label className="text-sm font-medium flex items-center gap-2">
                    <Landmark className="h-4 w-4 text-primary" /> Lender Bank Account
                    <span className="text-red-500">*</span>
                  </Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setBankSheetOpen(true)}
                    className="shrink-0"
                    disabled={!lenderId}
                  >
                    <Plus className="h-3.5 w-3.5 mr-1" /> Add
                  </Button>
                </div>

                {loadingBankAccounts ? (
                  <p className="text-xs text-muted-foreground py-2">Loading bank accounts...</p>
                ) : availableBankAccounts.length === 0 ? (
                  <div className="flex items-start gap-2 p-2.5 rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
                    <AlertTriangle className="h-3.5 w-3.5 text-amber-600 mt-0.5 shrink-0" />
                    <p className="text-xs text-amber-800 dark:text-amber-200">
                      No bank account saved for {lender || "this lender"}. Click <span className="font-medium">Add</span> to register one.
                    </p>
                  </div>
                ) : (
                  <div
                    className={cn(
                      "pr-1",
                      availableBankAccounts.length > 1 && "max-h-[220px] overflow-y-auto"
                    )}
                  >
                    <RadioGroup
                      value={lenderBankAccountId}
                      onValueChange={setLenderBankAccountId}
                      className="space-y-2"
                    >
                      {availableBankAccounts.map((a) => (
                        <BankAccountPickRow key={a.id} account={a} selected={lenderBankAccountId === a.id} />
                      ))}
                    </RadioGroup>
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 sm:justify-between">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
              Cancel
            </Button>
            {step === 1 ? (
              <Button onClick={() => setStep(2)} disabled={!isStep1Valid}>
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setStep(1)} disabled={isSubmitting}>
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Back
                </Button>
                <Button onClick={() => void handleConfirm()} disabled={!isStep2Valid || isSubmitting}>
                  {isSubmitting ? "Approving..." : "Confirm Approval"}
                </Button>
              </div>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <LenderBankAccountsSheet
        open={bankSheetOpen}
        onOpenChange={(nextOpen) => {
          setBankSheetOpen(nextOpen);
          if (!nextOpen) void fetchBankAccounts();
        }}
        selectedLenderId={lenderId}
        lender={lender}
        defaultAddOpen={availableBankAccounts.length === 0}
      />
    </>
  );
}
